import { useState, useCallback } from "react";
import type { Tables } from "@/integrations/supabase/types";
import type { Combo, ComboItem, ComboPizzaFlavor, ComboItemOption } from "./useCombos";
import type { ComboSlotWithProducts } from "./useInternalCombos";

type Product = Tables<"products">;

export interface CartItem {
  product: Product;
  quantity: number;
  notes?: string;
}

// Selection for slot-based combos (modern model)
export interface SlotSelection {
  slotId: string;
  slotLabel: string;
  selectedProducts: {
    productId: string;
    productName: string;
    price: number;
  }[];
}

// Combo cart item types
export interface ComboSelection {
  // For pizza combos (legacy): selected flavor product IDs
  selectedFlavors?: {
    productId: string;
    productName: string;
    price: number;
  }[];
  // For normal combos (legacy): map of combo_item_id to selected product
  selectedChoices?: {
    comboItemId: string;
    choiceLabel: string;
    productId: string;
    productName: string;
  }[];
  // For slot-based combos (modern model)
  slotSelections?: SlotSelection[];
}

export interface ComboCartItem {
  combo: Combo & { 
    pizzaFlavors?: ComboPizzaFlavor[]; 
    items?: (ComboItem & { options?: ComboItemOption[] })[];
    slots?: ComboSlotWithProducts[];
    useSlotModel?: boolean;
  };
  quantity: number;
  notes?: string;
  selection: ComboSelection;
  calculatedPrice: number;
}

export type OrderType = "table" | "tab" | "counter" | "delivery";

export interface DeliveryData {
  customerName: string;
  customerPhone: string;
  deliveryAddress: string;
  notes?: string;
}

export interface SelectedDestination {
  type: OrderType;
  number: string;
  id?: string;
  customerName?: string;
  deliveryData?: DeliveryData;
}

export interface OrderInfo {
  type: OrderType;
  tableNumber?: string;
  tabNumber?: string;
  customerName?: string;
  notes?: string;
  peopleCount?: number;
  waiterName?: string;
  deliveryData?: DeliveryData;
}

export function useCart() {
  const [items, setItems] = useState<CartItem[]>([]);
  const [comboItems, setComboItems] = useState<ComboCartItem[]>([]);
  const [destination, setDestination] = useState<SelectedDestination | null>(null);
  const [orderInfo, setOrderInfo] = useState<OrderInfo>({ type: "counter" });

  const selectDestination = useCallback((dest: SelectedDestination | null, peopleCount?: number) => {
    setDestination(dest);
    if (dest) {
      setOrderInfo({
        type: dest.type,
        tableNumber: dest.type === "table" ? dest.number : undefined,
        tabNumber: dest.type === "tab" ? dest.number : undefined,
        customerName: dest.customerName,
        peopleCount,
      });
    } else {
      setOrderInfo({ type: "counter" });
    }
  }, []);

  const addItem = useCallback((product: Product, quantity: number = 1, notes?: string) => {
    setItems((current) => {
      const existingIndex = current.findIndex(
        (item) => item.product.id === product.id && item.notes === notes
      );

      if (existingIndex >= 0) {
        const updated = [...current];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      }

      return [...current, { product, quantity, notes }];
    });
  }, []);

  const removeItem = useCallback((productId: string) => {
    setItems((current) => current.filter((item) => item.product.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setItems((current) =>
      current.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  }, [removeItem]);

  const updateItemNotes = useCallback((productId: string, notes: string) => {
    setItems((current) =>
      current.map((item) =>
        item.product.id === productId ? { ...item, notes } : item
      )
    );
  }, []);

  // Combo management functions
  const addCombo = useCallback((
    combo: ComboCartItem["combo"],
    selection: ComboSelection,
    calculatedPrice: number,
    quantity: number = 1,
    notes?: string
  ) => {
    setComboItems((current) => {
      // Create a unique key based on combo id and selection
      const selectionKey = JSON.stringify(selection);
      const existingIndex = current.findIndex(
        (item) => item.combo.id === combo.id && JSON.stringify(item.selection) === selectionKey && item.notes === notes
      );

      if (existingIndex >= 0) {
        const updated = [...current];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      }

      return [...current, { combo, quantity, notes, selection, calculatedPrice }];
    });
  }, []);

  const removeCombo = useCallback((index: number) => {
    setComboItems((current) => current.filter((_, i) => i !== index));
  }, []);

  const updateComboQuantity = useCallback((index: number, quantity: number) => {
    if (quantity <= 0) {
      removeCombo(index);
      return;
    }

    setComboItems((current) =>
      current.map((item, i) =>
        i === index ? { ...item, quantity } : item
      )
    );
  }, [removeCombo]);

  const updateComboNotes = useCallback((index: number, notes: string) => {
    setComboItems((current) =>
      current.map((item, i) =>
        i === index ? { ...item, notes } : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setComboItems([]);
    setDestination(null);
    setOrderInfo({ type: "counter" });
  }, []);

  const clearItems = useCallback(() => {
    setItems([]);
    setComboItems([]);
  }, []);

  const productSubtotal = items.reduce(
    (sum, item) => sum + Number(item.product.price) * item.quantity,
    0
  );

  const comboSubtotal = comboItems.reduce(
    (sum, item) => sum + item.calculatedPrice * item.quantity,
    0
  );

  const subtotal = productSubtotal + comboSubtotal;

  const productItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const comboItemCount = comboItems.reduce((sum, item) => sum + item.quantity, 0);
  const itemCount = productItemCount + comboItemCount;

  return {
    items,
    comboItems,
    destination,
    selectDestination,
    orderInfo,
    setOrderInfo,
    addItem,
    removeItem,
    updateQuantity,
    updateItemNotes,
    addCombo,
    removeCombo,
    updateComboQuantity,
    updateComboNotes,
    clearCart,
    clearItems,
    subtotal,
    itemCount,
  };
}
